/**
 * src/shared/lib/catalogQueue.ts
 *
 * HARDEN-02: Atomic IDB-backed catalog write queue — safe for concurrent tabs.
 *
 * Mirrors changeQueue.ts in structure; uses a DISTINCT IDB namespace:
 *   'estimatix-catalog-db' / 'catalog-queue'
 * to avoid any collision with the project sync queue ('estimatix-sync-db'/'queue').
 *
 * Key difference from changeQueue.ts: LWW-by-serviceId dedup at enqueue time.
 * enqueueCatalogChange() REPLACES any existing record for the same serviceId
 * so the queue holds at most one pending change per service. This prevents
 * DELETE-before-PUT inversion (T-28-02-02).
 *
 * All operations degrade gracefully on IDB failure (console.warn, never throw).
 * T-28-02-01 mitigation: IDB failure is logged; the fire-and-forget write-back
 * already attempted; the edit is retained in Zustand localStorage persist.
 */

import { update, get, set, createStore } from 'idb-keyval';
import type { CatalogChangeRecord } from '@/entities/sync/model/types';

// Distinct namespace — must NOT collide with 'estimatix-sync-db'/'queue' (changeQueue.ts)
const queueStore = createStore('estimatix-catalog-db', 'catalog-queue');
const QUEUE_KEY = 'estimatix-catalog-queue';

/**
 * Enqueue a catalog change record.
 * Atomic: uses update() for safe cross-tab concurrent enqueues.
 * LWW-by-serviceId: if a record for the same serviceId already exists, it is REPLACED.
 * This ensures the queue holds at most one pending change per service (T-28-02-02).
 * Degrades gracefully on IDB failure (T-28-02-01).
 */
export async function enqueueCatalogChange(record: CatalogChangeRecord): Promise<void> {
  try {
    await update<CatalogChangeRecord[]>(
      QUEUE_KEY,
      (queue) => {
        const existing = queue ?? [];
        // LWW-by-serviceId: replace any existing record for the same serviceId
        // (not id-based idempotency — that's the project queue's pattern).
        // This prevents DELETE-before-PUT inversion: the latest operation always wins.
        const filtered = existing.filter((r) => r.serviceId !== record.serviceId);
        return [...filtered, record];
      },
      queueStore,
    );
  } catch (e) {
    console.warn(
      '[catalogQueue] enqueueCatalogChange failed — IDB unavailable. ' +
      'Edit retained in Zustand localStorage persist; fire-and-forget write-back already attempted.',
      e,
    );
  }
}

/**
 * Read the current catalog queue without modifying it.
 * Returns empty array on IDB failure.
 */
export async function peekCatalogQueue(): Promise<CatalogChangeRecord[]> {
  try {
    return (await get<CatalogChangeRecord[]>(QUEUE_KEY, queueStore)) ?? [];
  } catch (e) {
    console.warn('[catalogQueue] peekCatalogQueue failed — returning empty queue.', e);
    return [];
  }
}

/**
 * Remove successfully flushed records by id. Atomic.
 * Idempotent: re-running with the same ids is a no-op.
 * Only removes ids in the flushedIds set; other records are preserved.
 * Only called AFTER server confirms receipt (2xx response) — T-28-02-01 mitigation.
 */
export async function dequeueCatalogFlushed(flushedIds: Set<string>): Promise<void> {
  try {
    await update<CatalogChangeRecord[]>(
      QUEUE_KEY,
      (queue) => (queue ?? []).filter((r) => !flushedIds.has(r.id)),
      queueStore,
    );
  } catch (e) {
    console.warn(
      '[catalogQueue] dequeueCatalogFlushed failed — queue may have stale records on retry.',
      e,
    );
  }
}

/**
 * Clear the entire catalog queue.
 * Only call after all records confirmed received by the server.
 */
export async function clearCatalogQueue(): Promise<void> {
  try {
    await set(QUEUE_KEY, [], queueStore);
  } catch (e) {
    console.warn('[catalogQueue] clearCatalogQueue failed — IDB unavailable.', e);
  }
}
